<template>
    <div id="chat-window-wrapper" ref="chatWindow">
        <Chat
            ref="chat"
            :contact="contact"
            :messageSyncRunning="messageSyncRunning"
            :messages="getMessages"
            :visitorTypingMessage="visitorTypingMessage"
            :show-header="showHeader"
            :debugModeEnabled="debugModeEnabled"
            @message="sendMessage"
            @debugModeChanged="handleDebugModeChanged"
        />
    </div>
</template>

<script>
import Chat from "./Chat.vue";
import UserInfoWidget from "./UserInfoWidget.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";
import { toNewMessagesModel } from "../../../utils/chat";
import { log2 } from "../../../utils/log2";
import { filter, map } from "rxjs/operators";
import { socketMessageStream } from "../../../utils/socketStream";
import _ from "lodash";

const log = log2("ChatWindow");

const HUMAN_MESSAGE_TYPES = new Set([
    "user",
    "user-media",
    "user-location",
    "visitor",
    "visitor-location",
    "visitor-media",
    "whatsapp-user",
    "whatsapp-user-media",
    "whatsapp-user-location",
    "instagram-user",
    "instagram-user-media",
    "instagram-user-location",
]);

const LOAD_MORE_MESSAGE = {
    messageType: "load-more",
    createdDate: new Date(-8640000000000000),
    uniqMessageId: "load-more",
};

export default {
    name: "ChatWindow",
    components: { UserInfoWidget, Chat },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
    },
    data: () => ({
        cancels: [],
        messageSyncRunning: true,
        lastReSyncIntervalId: null,
        visitorTypingMessage: null,
        typingTimeoutId: null,
        debugModeEnabled: false,
    }),
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
        }),
        ...mapGetters({
            getActiveConversationPendingMessages: "chat/getActiveConversationPendingMessages",
            getUser: "user/getUser", // bu kısımı asla kaldırma!
        }),
        storeMessages() {
            const contactId = this.$_.get(this.contact, "_id");
            if (!contactId) return [];
            const getter = this.$store.getters["contact/getMessagesForContact"];
            return typeof getter === "function" ? getter(contactId) : [];
        },
        storeDebugMessages() {
            const contactId = this.$_.get(this.contact, "_id");
            if (!contactId) return [];
            const getter = this.$store.getters["contact/getDebugMessagesForContact"];
            return typeof getter === "function" ? getter(contactId) : [];
        },
        conversationId() {
            return this.$_.get(this.contact, "lastConversation._id");
        },
        getMessages() {
            const storeMessages = this.storeMessages;
            
            // Debug mode açıksa debug mesajlarını ekle
            let allStoreMessages = storeMessages;
            if (this.debugModeEnabled && this.storeDebugMessages.length > 0) {
                // Normal ve debug mesajlarını birleştir, createdDate'e göre sırala
                allStoreMessages = this.$_.orderBy(
                    [...storeMessages, ...this.storeDebugMessages],
                    ["createdDate"],
                    ["asc"]
                );
            }
            
            const allMessages = allStoreMessages.length > 0
                ? [Object.assign({}, LOAD_MORE_MESSAGE, { conversationId: this.conversationId })].concat(allStoreMessages)
                : allStoreMessages;

            let processedMessages = toNewMessagesModel(allMessages, this);

            if (this.visitorTypingMessage) {
                processedMessages.push({
                    type: "typing",
                    data: {
                        text: this.visitorTypingMessage,
                    },
                });
            }

            let lastHumanMessage = null;
            for (let i = processedMessages.length - 1; i >= 0; i--) {
                const current = processedMessages[i];
                const messageType = this.$_.get(current, "raw.messageType");
                if (messageType && HUMAN_MESSAGE_TYPES.has(messageType)) {
                    lastHumanMessage = current;
                    break;
                }
            }

            if (this.contact?.lastConversation?.inactive && lastHumanMessage) {
                const startTS = this.$moment(
                    _.get(lastHumanMessage, "raw.createdDate", this.contact?.lastConversation?.createdDate)
                ).valueOf();
                const tookTimeInSeconds = this.$moment().diff(this.$moment(startTS), "seconds");
                const thresholdAsSeconds = (this.settings.inactiveThresholdTime || 5) * 60;
                const isIdle = tookTimeInSeconds >= thresholdAsSeconds * 0.95;
                if (isIdle) {
                    processedMessages.push({
                        type: "idle-time",
                        data: {
                            startTimestamp: startTS,
                            isLive: true,
                        },
                    });
                }
            }
            return this.$_.orderBy(processedMessages, ["raw.createdDate"], ["asc"]);
        },
    
    },
    mounted() {
        this.initConversation();
    },
    methods: {
        ...mapActions({
            listContactMessages: "contact/listContactMessages",
            loadLastNMessage: "chat/loadLastNMessage",
        }),
        sendMessage(message) {
            const contactId = this.$_.get(this.contact, "_id");
            if (contactId) {
                this.$store.dispatch("contact/appendMessage", {
                    contactId: contactId,
                    message: {
                        ...message,
                        status: "pending",
                        createdDate: new Date().toISOString(),
                        uniqMessageId: message.uniqMessageId || `pending_${Date.now()}_${Math.random()}`,
                    },
                });
            }
        },
        handleDebugModeChanged(enabled) {
            this.debugModeEnabled = enabled;
        },
        initConversation() {
            if (this.contact.lastConversation) {
                if (this.typingTimeoutId) {
                    clearTimeout(this.typingTimeoutId);
                    this.typingTimeoutId = null;
                }
                this.visitorTypingMessage = null;
                this.syncMessages(this.contact.lastConversation._id, this.contact?._id, false, true);
                this.setupResyncMessageWorker(this.contact.lastConversation._id);
                this.initListeners();
            }
        },
        initListeners() {
            const conversationId = this.$_.get(this.contact, "lastConversation._id");
            const contactId = this.$_.get(this.contact, "_id");
            if (!conversationId) return;
            if (!contactId) return;
            this.cancels.forEach((cb) => cb());
            const loadMoreSubscriber = SYSTEM_PUBSUB.stream
                .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.ACTIVE_CONVERSATION_LOAD_MORE))
                .subscribe(async () => {
                    this.syncMessages(conversationId, contactId, true).finally(() => {
                        try {
                            document.getElementsByClassName("sc-message-list")[0].scrollTop = 0;
                        } catch (e) {
                            log.error("scroll error", e);
                        }
                    });
                });

            const typingSubscriber = socketMessageStream
                .pipe(filter((x) => ["message"].includes(x.cmd)))
                .subscribe(({ data }) => {
                    if (this.contact.lastConversation && this.contact?.lastConversation?._id !== data.conversationId) {
                        return;
                    }

                    if (data.type === "customer-typing") {
                        if (this.settings.writing === 1) {
                            this.visitorTypingMessage = data.message;

                            if (this.typingTimeoutId) {
                                clearTimeout(this.typingTimeoutId);
                            }

                            this.typingTimeoutId = setTimeout(() => {
                                this.visitorTypingMessage = null;
                                this.typingTimeoutId = null;
                            }, 3000);
                        }
                        return;
                    }
                    else if (
                        data.messageType === "visitor" &&
                        data.visitorId === this.visitorTypingMessage?.visitorId
                    ) {
                        this.visitorTypingMessage = null;
                        if (this.typingTimeoutId) {
                            clearTimeout(this.typingTimeoutId);
                            this.typingTimeoutId = null;
                        }
                    }
                });

            this.cancels.push(() => {
                loadMoreSubscriber.unsubscribe();
                typingSubscriber.unsubscribe();
                if (this.typingTimeoutId) {
                    clearTimeout(this.typingTimeoutId);
                    this.typingTimeoutId = null;
                }
            });
        },
        async syncMessages(conversationId, contactId, resyncMode = false, cache = false) {
            const activeConversationId = this.$_.get(this.contact, "lastConversation._id");
            log("syncMessages", activeConversationId);
            conversationId = conversationId || activeConversationId;
            if (conversationId !== activeConversationId) {
                log.warn("syncMessages activeConversationId changed", { conversationId, activeConversationId });
                return;
            }
            if (conversationId && contactId) {
                if (!resyncMode) this.messageSyncRunning = true;
                return this.listContactMessages({ contactId, conversationId, cache })
                    .then((newMessageList) => {
                        if (newMessageList.length > 0) {
                            this.$store.dispatch("contact/appendMultiMessage", {
                                contactId: contactId,
                                messages: newMessageList,
                            });
                        }
                    })
                    .finally(() => {
                        if (!resyncMode) this.messageSyncRunning = false;
                    });
            }
        },
        setupResyncMessageWorker(conversationId) {
            if (this.lastReSyncIntervalId) clearInterval(this.lastReSyncIntervalId);
            const sync = async () => {
                const contactId = _.get(this.contact, "_id");
                const messages = await this.loadLastNMessage({
                    contactId,
                    conversationId,
                    take: 50,
                }).catch(() => []);
                const currentChatId = _.get(this.contact, "lastConversation._id");

                if (currentChatId === conversationId && contactId && messages.length > 0) {
                    const storeMessages = this.storeMessages || [];
                    const diffList = _.differenceBy(messages, storeMessages, "uniqMessageId");
                    if (diffList.length > 0) {
                        log.warn("chatwindow messages are not sync", {
                            newMessageList: messages,
                            storeMessages: storeMessages,
                            diffList,
                        });
                        this.$store.dispatch("contact/appendMultiMessage", {
                            contactId: contactId,
                            messages: diffList,
                        });
                    } else {
                        log("chatwindow messages are sync");
                    }
                }
            };
            sync();
            const intervalId = setInterval(sync, 3000);
            if (this.lastReSyncIntervalId) clearInterval(this.lastReSyncIntervalId);
            this.lastReSyncIntervalId = intervalId;
            this.cancels.push(() => clearInterval(intervalId));
        },
    },
    beforeDestroy() {
        this.cancels.forEach((fn) => fn());
        if (this.typingTimeoutId) {
            clearTimeout(this.typingTimeoutId);
            this.typingTimeoutId = null;
        }
    },
    watch: {
        contact: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal?.lastConversation?._id !== oldVal?.lastConversation?._id) this.initConversation();
            },
        },
        socketConnected(isConnected) {
            if (isConnected) {
                if (this.contact) {
                    this.syncMessages(this.contact?.lastConversation?._id, this.contact?._id);
                }
            }
        },
    },
};
</script>
<style lang="scss" scoped>
#chat-window-wrapper {
    height: 100%;
    width: 100%;
}
</style>
